import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';

import { TranslationDialogDataModel } from '~components/dialogs/translation-dialog/models/translation-dialog-data.model';
import { TranslationDialogComponent } from '~components/dialogs/translation-dialog/translation-dialog.component';
import { BaseControlComponent } from '~components/form/base-control/base-control.component';
import { ErrorTooltipDirective } from '~core/directives/error-tooltip.directive';
import { TranslationService } from '~core/services/translation.service';
import { ArcFormControl, ArcFormControlInterface } from '~core/utils/arc-form-control';

@Component({
    selector: 'arc-translation-input',
    templateUrl: './translation-input.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatFormField,
        ErrorTooltipDirective,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        MatIconButton,
        MatSuffix,
        MatIcon,
        TranslocoPipe
    ]
})
export class TranslationInputComponent extends BaseControlComponent implements OnInit {
    @Input() controlDe!: ArcFormControlInterface;
    @Input() controlEn!: ArcFormControlInterface;
    @Input() controlFr!: ArcFormControlInterface;
    @Input() controlIt!: ArcFormControlInterface;

    override control = new ArcFormControl('');

    private controlPreviousValue = '';

    private readonly translationService = inject(TranslationService);
    private readonly matDialog = inject(MatDialog);

    override ngOnInit(): void {
        super.ngOnInit();
        const currentValue = !!this.currentLanguageControl.value ? this.currentLanguageControl.value : this.controlDe.value;

        this.control.setValue(currentValue);
        this.controlPreviousValue = currentValue;
        const valueChangesSub = this.control.valueChanges.subscribe(val => {
            this.currentLanguageControl.setValue(val);
            this.currentLanguageControl.markAsDirty();

            if (this.currentLanguageControl !== this.controlDe) {
                // if german was already in sync with the current language, keep it in sync
                if (this.controlDe.value === this.controlPreviousValue) {
                    this.controlDe.setValue(val);
                    if (!this.controlDe.dirty) {
                        this.controlDe.markAsDirty();
                    }
                }
            }

            this.controlPreviousValue = val;
        });

        // the main control uses the validators from the DE control
        this.control.addValidators(this.controlDe.validator ?? []);

        // since DE has the validation, when DE control status changes, we need to set errors on this control too
        const deStatusChangesSub = this.controlDe.statusChanges.subscribe(() => {
            this.control.setErrors(this.controlDe.errors);
        });

        // since DE has the validation, when DE is touched, we need to set errors and mark this control as touched too
        const deTouchedSub = this.controlDe.onTouched.subscribe(() => {
            this.control.setErrors(this.controlDe.errors);
            this.control.markAsTouched();
        });

        this.addSubscriptions(valueChangesSub, deStatusChangesSub, deTouchedSub);
    }

    get currentLanguageCode(): string {
        return this.translationService.current.code;
    }

    get currentLanguageControl(): AbstractControl {
        switch (this.currentLanguageCode) {
            case 'en':
                return this.controlEn;
            case 'fr':
                return this.controlFr;
            case 'it':
                return this.controlIt;
            default:
                return this.controlDe;
        }
    }

    openTranslationDialog(): void {
        const dialogref = this.matDialog.open(TranslationDialogComponent, {
            data: new TranslationDialogDataModel({
                labelKey: this.labelKey,
                label: this.label,
                valueDe: this.controlDe.value,
                valueEn: this.controlEn.value,
                valueFr: this.controlFr.value,
                valueIt: this.controlIt.value,
                currentLanguageCode: this.currentLanguageCode
            }),
            width: '500px',
            maxWidth: '98vw',
            maxHeight: '98vsh'
        });

        dialogref.afterClosed().subscribe(data => {
            if (!data) {
                return;
            }
            let germanValue = data.valueDe;
            if (!germanValue) {
                switch (this.currentLanguageCode) {
                    case 'en':
                        germanValue = data.valueEn;
                        break;
                    case 'fr':
                        germanValue = data.valueFr;
                        break;
                    case 'it':
                        germanValue = data.valueIt;
                        break;
                }
            }
            if (this.controlDe.value !== germanValue) {
                this.controlDe.setValue(germanValue);
                this.controlDe.markAsDirty();
            }
            if (this.controlEn.value !== data.valueEn) {
                this.controlEn.setValue(data.valueEn);
                this.controlEn.markAsDirty();
            }
            if (this.controlFr.value !== data.valueFr) {
                this.controlFr.setValue(data.valueFr);
                this.controlFr.markAsDirty();
            }
            if (this.controlIt.value !== data.valueIt) {
                this.controlIt.setValue(data.valueIt);
                this.controlIt.markAsDirty();
            }

            // if value was changed through dialog, do not sync german with current language
            this.controlPreviousValue = this.currentLanguageControl.value;
            this.control.setValue(this.currentLanguageControl.value);
        });
    }
}
